import React, { FunctionComponent, useState } from 'react'
import { defineMessage, useIntl } from 'react-intl'
import { View, ViewStyle } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { AURORA_CDN_ASSETS_LINK_BASE } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'

import abstractFace from '../../assets/abstract_face.png'
import abstractFaceAlt1 from '../../assets/abstract_face_alt1.png'
import abstractFaceAlt2 from '../../assets/abstract_face_alt2.png'
import { BodyText } from '../../atoms/bodyText/BodyText'
import { IS_WEB } from '../../constants'
import { BodyTextSize, TitleSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { RibbonHeartIllustration } from '../illustrations/RibbonHeartIllustration'
import { Image } from '../image/Image'
import { Title } from '../title/Title'

export interface DefaultAvatarProps {
  displayName: string
  size?: number
  backgroundColor?: string
  fontColor?: string
  fontSize?: BodyTextSize
  style?: ViewStyle
  shape?: AvatarShape
  testId?: string
  borderRadius?: number
  fill?: boolean
}

export interface BadgeAvatarProps {
  displayName: string
  size?: number
  style?: ViewStyle
  shape?: AvatarShape
  testId?: string
}

const DefaultIllustrations = [abstractFace, abstractFaceAlt1, abstractFaceAlt2]
const AuroraDefaultIllustrations = [
  `${AURORA_CDN_ASSETS_LINK_BASE}/defaultAvatars/defaultAvatarGreen1.png`,
  `${AURORA_CDN_ASSETS_LINK_BASE}/defaultAvatars/defaultAvatarGreen2.png`,
  `${AURORA_CDN_ASSETS_LINK_BASE}/defaultAvatars/defaultAvatarOrange1.png`,
  `${AURORA_CDN_ASSETS_LINK_BASE}/defaultAvatars/defaultAvatarOrange2.png`,
]

export enum AvatarShape {
  CIRCULAR = 'circular',
  RECTANGULAR = 'rectangular',
}

export interface AvatarProps {
  details: AvatarDetails
  size?: number
  useLocalSrc?: boolean
  shape?: AvatarShape
  accessibilityLabel: string
  borderRadius?: number
  providerID?: string
  unsetBorderRadius?: boolean
  hasBadge?: boolean
  badgeSize?: number
  topBadgeMargin?: string
  leftBadgeMargin?: string
  fill?: boolean
}

export interface AvatarDetails {
  src: string
  displayName: string
  hide?: boolean
}
export const avatarAltTextGeneric = defineMessage({
  defaultMessage: 'Photograph of {name}',
  description: 'Alt text description for the photograph of the person in the avatar',
})

export const avatarAltTextProvider = defineMessage({
  defaultMessage: 'Photograph of provider {name}',
  description: 'Alt text description for the photograph of the provider in the avatar',
})

export const avatarAltTextForDefaultIllustration = defineMessage({
  defaultMessage: 'Illustration used when the provider has no photograph to display',
  description:
    'Alt text description for the placeholder illustration used when the provider does not have a valid photograph to display',
})

interface AvatarContainerProps {
  size: number
  shape: AvatarShape
  borderRadius?: number
  unsetBorderRadius?: boolean
  fill?: boolean
}
const AvatarContainer = styled(Image)<AvatarContainerProps>(({
  theme: {
    breakpoints: { isMobileSized },
  },
  size,
  shape,
  borderRadius,
  unsetBorderRadius,
  fill,
}) => {
  let borderRadiusConstant = 2
  if (shape === AvatarShape.RECTANGULAR) {
    if (isMobileSized || !IS_WEB) {
      borderRadiusConstant = 4.66
    } else {
      borderRadiusConstant = 4.75
    }
  }
  return {
    ...(fill
      ? { flex: 1, ...(IS_WEB && { objectFit: 'cover', objectPosition: 'center' }) }
      : { width: `${size}px`, height: `${size}px` }),
    borderRadius: unsetBorderRadius ? 'unset' : (borderRadius ?? `${size / borderRadiusConstant}px`),
    borderTopRightRadius: unsetBorderRadius ? `8px` : (borderRadius ?? `${size / borderRadiusConstant}px`),
  }
})

const DefaultAvatarContainer = styled.View<{
  theme: ThemeType
  size: number
  backgroundColor: string
  shape: AvatarShape
  borderRadius?: number
  fill?: boolean
}>(
  ({
    theme: {
      breakpoints: { isMobileSized },
    },
    size,
    backgroundColor,
    shape,
    borderRadius,
    fill,
  }) => {
    let borderRadiusConstant = 2
    if (shape === AvatarShape.RECTANGULAR) {
      if (isMobileSized || !IS_WEB) {
        borderRadiusConstant = 4.66
      } else {
        borderRadiusConstant = 4.75
      }
    }
    return {
      ...(fill ? { flex: 1 } : { width: `${size}px`, height: `${size}px` }),
      backgroundColor,
      borderRadius: borderRadius ?? `${size / borderRadiusConstant}px`,
      justifyContent: 'center',
      position: 'relative',
      overflow: 'hidden',
    }
  },
)

const BadgeContainer = styled.View<{
  top?: string | number
  left?: string | number
}>(({ top, left }) => ({
  position: 'absolute',
  top,
  left,
}))

const AuroraDefaultAvatar: FunctionComponent<Omit<DefaultAvatarProps, 'fontColor'> & { source?: string }> = ({
  displayName = '',
  size = 54,
  style,
  shape = AvatarShape.CIRCULAR,
  testId = 'DefaultAvatar',
  borderRadius = 16,
  source = AuroraDefaultIllustrations[0],
  fill,
}) => {
  const [firstName, lastName] = displayName.toUpperCase().split(' ')
  const { formatMessage } = useIntl()
  const { colors } = useTheme() as ThemeType
  /* eslint-disable-next-line formatjs/no-literal-string-in-jsx */
  const initials = firstName && lastName ? `${firstName.charAt(0)}${lastName.charAt(0)}` : 'N/A'

  const effectiveBorderRadius = shape === AvatarShape.CIRCULAR ? undefined : borderRadius

  return (
    <DefaultAvatarContainer
      backgroundColor='transparent'
      size={size}
      shape={shape}
      fill={fill}
      testID={tID(testId)}
      style={style as ViewStyle}
      accessibilityLabel={formatMessage(avatarAltTextGeneric, { name: displayName })}
      borderRadius={effectiveBorderRadius}
    >
      <AvatarContainer
        size={size}
        source={{ uri: source }}
        shape={shape}
        alt=''
        contentFit={fill ? 'cover' : undefined}
        style={
          fill
            ? {
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                width: '100%',
                height: '100%',
                borderRadius: 0,
                borderTopRightRadius: 0,
              }
            : { position: 'absolute', top: 0, left: 0 }
        }
        borderRadius={fill ? 0 : effectiveBorderRadius}
      />
      <BodyText
        text={initials}
        color={colors.components.avatar.defaultAvatarTextColor}
        textAlign='center'
        style={{
          fontSize: Math.round(size * (42 / 144)),
          lineHeight: Math.round(size * (50.4 / 144)),
          fontWeight: '300',
        }}
      />
    </DefaultAvatarContainer>
  )
}

export const DefaultAvatar: FunctionComponent<DefaultAvatarProps> = ({
  displayName = '',
  backgroundColor,
  size = 54,
  fontSize = BodyTextSize.LARGE,
  fontColor,
  style,
  shape = AvatarShape.CIRCULAR,
  testId = 'DefaultAvatar',
  borderRadius = 16,
  fill,
}) => {
  // assumes displayName always has first and last name split by space
  const [firstName, lastName] = displayName.toUpperCase().split(' ')
  const { formatMessage } = useIntl()
  const { colors, isAuroraTheme } = useTheme() as ThemeType

  if (isAuroraTheme) {
    return (
      <AuroraDefaultAvatar
        displayName={displayName}
        size={size}
        style={style}
        shape={shape}
        testId={testId}
        borderRadius={borderRadius}
        fill={fill}
      />
    )
  }

  return (
    <DefaultAvatarContainer
      backgroundColor={backgroundColor || colors.backgroundHighlightPrimary}
      size={size}
      shape={shape}
      fill={fill}
      testID={tID(testId)}
      style={style}
      accessibilityLabel={formatMessage(avatarAltTextGeneric, { name: displayName })}
      borderRadius={borderRadius}
    >
      <BodyText
        size={fontSize}
        /* eslint-disable-next-line formatjs/no-literal-string-in-jsx */
        text={firstName && lastName ? `${firstName.charAt(0)}${lastName.charAt(0)}` : 'N/A'}
        color={fontColor || colors.textActive}
        textAlign='center'
      />
    </DefaultAvatarContainer>
  )
}

/**
 * Standard round Avatar Icon that will display
 * the image with the provided uri string or will default if the image source
 * is an empty string
 */
export const Avatar: FunctionComponent<AvatarProps> = ({
  details: { src, displayName, hide } = { src: '', displayName: '' },
  useLocalSrc, // useLocalSrc is when we have a local imported image while using 'require(...some png)'
  size = 48,
  shape = AvatarShape.CIRCULAR,
  accessibilityLabel,
  borderRadius,
  providerID,
  unsetBorderRadius,
  hasBadge = false,
  badgeSize,
  topBadgeMargin,
  leftBadgeMargin,
  fill,
}) => {
  const [isDefaultAvatarShown, setIsDefaultAvatarShown] = useState(!src)
  const { formatMessage } = useIntl()
  const { isAuroraTheme } = useTheme() as ThemeType
  const topProviderBadge = hasBadge ? (
    <BadgeContainer top={topBadgeMargin} left={leftBadgeMargin}>
      <RibbonHeartIllustration width={badgeSize} height={badgeSize} />
    </BadgeContainer>
  ) : null

  const fillStyle = fill ? { flex: 1 } : undefined

  if (hide) {
    return null
  } else {
    // There are 3 different default avatar illustrations. The illustrations need to be evenly
    // distributed amongst providers who do not have a valid photograph. For example, on the provider results page,
    // the providers without photographs should not all have the same default avatar illustration.
    // The illustrations should also be assigned consistently so that whenever a client sees a provider's avatar,
    // it will always show the same illustration.
    // The `providerID` is a uuid which consists of hexadecimal values so the first value in the uuid is
    // taken and converted into an integer using `parseInt`, then mod by the number of different default avatar
    // illustrations (3) to get an illustration assignment.
    const defaultIllustrationId = providerID ? parseInt(providerID[0], 16) % DefaultIllustrations.length : null
    const auroraIllustrationId = providerID ? parseInt(providerID[0], 16) % AuroraDefaultIllustrations.length : 0
    const defaultAvatar = (
      <View style={{ display: isDefaultAvatarShown ? 'flex' : 'none', ...fillStyle }}>
        {isAuroraTheme ? (
          <AuroraDefaultAvatar
            displayName={displayName}
            size={size}
            shape={shape}
            borderRadius={borderRadius}
            source={AuroraDefaultIllustrations[auroraIllustrationId]}
            fill={fill}
          />
        ) : defaultIllustrationId !== null ? (
          <AvatarContainer
            size={size}
            source={DefaultIllustrations[defaultIllustrationId]}
            shape={shape}
            alt={formatMessage(avatarAltTextForDefaultIllustration)}
            accessibilityLabel={formatMessage(avatarAltTextForDefaultIllustration)}
            testID={tID('Avatar-default-illustration')}
            unsetBorderRadius={unsetBorderRadius}
            borderRadius={borderRadius}
            fill={fill}
          />
        ) : (
          <DefaultAvatar displayName={displayName} size={size} shape={shape} borderRadius={borderRadius} fill={fill} />
        )}
        {topProviderBadge}
      </View>
    )

    const photoAvatar = (
      <View style={{ display: isDefaultAvatarShown ? 'none' : 'flex', ...fillStyle }}>
        <AvatarContainer
          size={size}
          source={useLocalSrc ? src : { uri: src }}
          shape={shape}
          accessibilityLabel={accessibilityLabel}
          testID={tID('Avatar')}
          borderRadius={borderRadius}
          alt={accessibilityLabel}
          contentFit={fill ? 'cover' : undefined}
          onError={() => {
            setIsDefaultAvatarShown(true)
          }}
          unsetBorderRadius={unsetBorderRadius}
          fill={fill}
        />
        {topProviderBadge}
      </View>
    )
    return (
      <>
        {defaultAvatar}
        {photoAvatar}
      </>
    )
  }
}

export const BadgeAvatar: FunctionComponent<BadgeAvatarProps> = ({
  displayName = '',
  size,
  style,
  shape = AvatarShape.CIRCULAR,
  testId,
}) => {
  const { formatMessage, locale } = useIntl()
  const { colors, breakpoints } = useTheme() as ThemeType
  const avatarSize = size || (breakpoints.isMobileSized ? 40 : 54)
  // Callers append a localized "(You)" suffix to distinguish the account holder, so strip any
  // trailing parenthetical rather than the English literal.
  const nameParts = displayName
    .replace(/\s*\([^)]*\)\s*$/, '')
    .trim()
    .split(' ')
    .filter(Boolean)
  const initialParts = nameParts.length > 1 ? [nameParts[0], nameParts.at(-1)!] : nameParts
  const displayInitial = initialParts.length
    ? initialParts.map((n) => n.charAt(0).toLocaleUpperCase(locale)).join('')
    : 'N/A'
  return (
    <DefaultAvatarContainer
      backgroundColor={colors.components.badge.neutral.background}
      size={avatarSize}
      shape={shape}
      testID={tID(testId)}
      style={style}
      accessibilityLabel={formatMessage(avatarAltTextGeneric, { name: displayName })}
    >
      <Title
        size={breakpoints.isMobileSized ? TitleSize.XSMALL : TitleSize.MEDIUM}
        text={displayInitial}
        color={colors.textSecondary}
        style={{ textAlign: 'center' }}
      />
    </DefaultAvatarContainer>
  )
}
